import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { shortTitle, shortDescription } from "@/utils/metaLength";
import axios from "axios";
import { Star, MessageCircle, ArrowRight } from "lucide-react";
import { getLangFromUrl, setLangInUrl, useHreflang, updateCanonical } from "@/utils/seoLang";
import { CITIES } from "@/cityConfig";
import { t } from "@/translations";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CityReviews from "@/components/CityReviews";
import CityBlogSection from "@/components/CityBlogSection";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const DEFAULT_LANGS = ["ru", "en"];

const API = "/api";

const COPY = {
  ru: {
    eyebrow: "Отзывы клиентов",
    titleTail: "— отзывы о доставке цветов",
    subtitle: "Реальные впечатления клиентов. Оставьте свой отзыв — мы публикуем все проверенные отзывы.",
    based: (n) => `на основе ${n} ${plural(n, "отзыва", "отзывов", "отзывов")}`,
    catalog: "Смотреть каталог",
    whatsapp: "Написать в WhatsApp",
    elsewhere: "Мы также доставляем в",
  },
  en: {
    eyebrow: "Customer reviews",
    titleTail: "— flower delivery reviews",
    subtitle: "Real customer impressions. Leave your review — we publish every verified review.",
    based: (n) => `based on ${n} review${n === 1 ? "" : "s"}`,
    catalog: "View catalog",
    whatsapp: "Message on WhatsApp",
    elsewhere: "We also deliver in",
  },
};
function plural(n, few, many, none) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

export default function ReviewsPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const city = CITIES[citySlug];
  const availableLanguages = city?.languages || DEFAULT_LANGS;
  const [lang, setLangState] = useState(() => getLangFromUrl(searchParams, availableLanguages));
  const [settings, setSettings] = useState(null);
  const [about, setAbout] = useState(null);
  const [agg, setAgg] = useState({ count: 0, average: 0 });

  const setLang = (nl) => { setLangState(nl); setLangInUrl(nl, searchParams, setSearchParams); };
  useHreflang(`/${citySlug}/reviews`, availableLanguages);

  useEffect(() => { if (!city) navigate("/antalya", { replace: true }); }, [city, navigate]);

  const cLang = lang === "en" ? "en" : "ru";
  const c = COPY[cLang];
  const cityName = lang === "en" ? city?.nameEn : (lang === "tr" ? (city?.nameTr || city?.nameEn) : city?.name);
  const cityPrep = lang === "en" ? city?.namePrepositionalEn : (lang === "tr" ? (city?.namePrepositionalTr || city?.namePrepositionalEn) : city?.namePrepositional);

  useEffect(() => {
    if (!city) return;
    axios.get(`${API}/settings`).then((r) => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/reviews/about/${citySlug}?lang=${cLang}`).then((r) => setAbout(r.data && r.data.heading ? r.data : null)).catch(() => setAbout(null));
    axios.get(`${API}/reviews?city=${citySlug}`).then((r) => setAgg({ count: r.data.count || 0, average: r.data.average || 0 })).catch(() => {});
  }, [city, citySlug, cLang]);

  useEffect(() => {
    if (!city) return;
    const title = lang === "en"
      ? `Reviews — Flower Delivery in ${cityName} | YUCCA Flowers`
      : `Отзывы — доставка цветов ${cityPrep} | YUCCA Flowers`;
    const description = lang === "en"
      ? `Real customer reviews about YUCCA flower delivery in ${cityName}${agg.count ? ` — rating ${agg.average.toFixed(1)}/5 from ${agg.count} reviews` : ""}. Leave your own verified review.`
      : `Реальные отзывы клиентов о доставке цветов YUCCA ${cityPrep}${agg.count ? ` — рейтинг ${agg.average.toFixed(1)}/5 на основе ${agg.count} отзывов` : ""}. Оставьте свой проверенный отзыв.`;
    const url = `https://yuccaflower.com/${citySlug}/reviews${lang !== "ru" ? `?lang=${lang}` : ""}`;
    const ogImage = "https://yuccaflower.com/og-banner.jpg";

    document.title = shortTitle(title);
    const setMeta = (sel, attr, val) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', "content", shortDescription(description));
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:image"]', "content", ogImage);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", ogImage);
    updateCanonical(`/${citySlug}/reviews`, availableLanguages);
    document.documentElement.lang = lang;
  }, [city, lang, citySlug, cityName, cityPrep, agg.count, agg.average, availableLanguages]);

  if (!city) return null;

  const openWhatsApp = () => {
    const phone = settings?.whatsapp?.replace(/[^0-9]/g, "") || "905316224378";
    const msg = t("wa.general", lang, { city: cityPrep });
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const sameCountry = Object.values(CITIES).filter((x) => x.country === city.country && x.slug !== city.slug).slice(0, 8);

  return (
    <>
      <Header
        settings={settings}
        currentCity={city}
        onCityChange={(slug) => navigate(`/${slug}`)}
        selectedCurrency={city.currency}
        onCurrencyChange={() => {}}
        lang={lang}
        onLangChange={setLang}
        t={(key, vars) => t(key, lang, vars)}
        availableLanguages={availableLanguages}
        solid
      />

      <main className="pt-20">
        {/* Hero */}
        <section className="bg-gradient-to-b from-pink-50 to-white pt-12 sm:pt-16 pb-6 text-center px-4" data-testid="reviews-page-hero">
          <span className="inline-block bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            {c.eyebrow}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900" style={{ fontFamily: "Playfair Display, serif" }}>
            {cityName} <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500">{c.titleTail}</span>
          </h1>
          {agg.count > 0 && (
            <div className="flex items-center justify-center gap-3 mt-4" data-testid="reviews-page-aggregate">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className={`w-5 h-5 ${i <= Math.round(agg.average) ? "fill-amber-400 text-amber-400" : "fill-gray-200 text-gray-200"}`} />
                ))}
              </div>
              <span className="text-lg font-bold text-gray-900">{agg.average.toFixed(1)}</span>
              <span className="text-gray-500 text-sm">· {c.based(agg.count)}</span>
            </div>
          )}
          <p className="text-gray-500 max-w-xl mx-auto text-base sm:text-lg mt-4">{c.subtitle}</p>
        </section>

        {/* Form first, then reviews list */}
        <CityReviews city={citySlug} lang={lang} formFirst hideHeader />

        {/* Unique per-city about block */}
        {about && (
          <section className="py-12 sm:py-16 bg-gradient-to-b from-white to-pink-50" data-testid="reviews-page-about">
            <div className="max-w-3xl mx-auto px-5 sm:px-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-5" style={{ fontFamily: "Playfair Display, serif" }}>{about.heading}</h2>
              <div className="space-y-4">
                {about.paragraphs.map((p, i) => (
                  <p key={i} className="text-gray-600 leading-relaxed text-base">{p}</p>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <a href={`/${citySlug}${lang !== "ru" ? `?lang=${lang}` : ""}`}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white text-sm font-semibold shadow-lg shadow-pink-200 hover:scale-[1.02] transition-all"
                  data-testid="reviews-page-catalog-btn">
                  {c.catalog} <ArrowRight className="w-4 h-4" />
                </a>
                <button onClick={openWhatsApp}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#25d366] hover:bg-[#20bd5a] text-white text-sm font-semibold shadow-lg shadow-green-200 hover:scale-[1.02] transition-all"
                  data-testid="reviews-page-whatsapp-btn">
                  <MessageCircle className="w-4 h-4" /> {c.whatsapp}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Useful articles for this city (after SEO text) */}
        <CityBlogSection citySlug={citySlug} lang={lang} />

        {/* Cross-links: reviews in other cities of the same country */}
        {sameCountry.length > 0 && (
          <section className="py-12 bg-white" data-testid="reviews-page-crosslinks">
            <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
              <h2 className="text-lg font-semibold text-gray-500 mb-5">{c.elsewhere}</h2>
              <div className="flex flex-wrap justify-center gap-3">
                {sameCountry.map((x) => (
                  <a key={x.slug} href={`/${x.slug}/reviews${lang !== "ru" ? `?lang=${lang}` : ""}`}
                    className="px-4 py-2 rounded-full border border-gray-200 text-gray-700 text-sm hover:border-pink-300 hover:text-pink-600 transition"
                    data-testid={`crosslink-${x.slug}`}>
                    {lang === "en" ? x.nameEn : (lang === "tr" ? (x.nameTr || x.nameEn) : x.name)}
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer settings={settings} city={city} lang={lang} t={(key, vars) => t(key, lang, vars)} />
      <FloatingWhatsApp onClick={openWhatsApp} />
    </>
  );
}
